/**
 * Video plugin: captures basic HTML5 video engagement events.
 *
 * Emits:
 * - `video_start`
 * - `video_progress` (milestones)
 * - `video_complete`
 */
import { clampPercent } from "../lib/redact";
import type { PluginContext } from "../lib/types";

import { registerPlugin } from "./register";

type VideoProgressState = {
  started: boolean;
  milestones: Set<number>;
};

function getMilestones(ctx: PluginContext) {
  const raw = ctx.config.video.progressMilestones ?? [];
  const set = new Set<number>();
  raw.forEach((value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return;
    const rounded = Math.round(clampPercent(num));
    if (rounded <= 0 || rounded >= 100) return;
    set.add(rounded);
  });
  return Array.from(set).sort((a, b) => a - b);
}

function getVideoId(ctx: PluginContext, video: HTMLVideoElement) {
  const explicit =
    video.getAttribute("data-prospix-video-id") ||
    video.getAttribute("data-video-id") ||
    "";
  if (explicit) return explicit;

  const src = video.currentSrc || video.src || "";
  if (!src) return undefined;
  const sanitized = ctx.redact.sanitizeUrl(src);
  return sanitized ? ctx.redact.hashString(sanitized) : undefined;
}

function getVideoMeta(ctx: PluginContext, video: HTMLVideoElement) {
  const duration = Number.isFinite(video.duration) ? video.duration : undefined;
  return {
    video_id: getVideoId(ctx, video),
    duration_sec: duration ? Math.round(duration) : undefined,
    muted: video.muted || undefined,
  } as Record<string, unknown>;
}

registerPlugin("video", (ctx: PluginContext) => {
  const milestones = getMilestones(ctx);
  const state = new WeakMap<HTMLVideoElement, VideoProgressState>();

  const getState = (video: HTMLVideoElement): VideoProgressState => {
    const existing = state.get(video);
    if (existing) return existing;
    const next: VideoProgressState = {
      started: false,
      milestones: new Set(milestones),
    };
    state.set(video, next);
    return next;
  };

  const onPlay = (event: Event) => {
    if (!ctx.canTrack("analytics")) return;
    if (!ctx.canTrackCategory("video_analytics")) return;
    const video =
      event.target instanceof HTMLVideoElement ? event.target : null;
    if (!video) return;
    const s = getState(video);
    if (s.started) return;
    s.started = true;
    ctx.track("video_start", getVideoMeta(ctx, video));
  };

  const onTimeUpdate = (event: Event) => {
    if (!ctx.canTrack("analytics")) return;
    if (!ctx.canTrackCategory("video_analytics")) return;
    const video =
      event.target instanceof HTMLVideoElement ? event.target : null;
    if (!video) return;
    const s = getState(video);
    if (!s.milestones.size) return;
    const duration = video.duration;
    if (!Number.isFinite(duration) || duration <= 0) return;
    const currentTime = video.currentTime;
    if (!Number.isFinite(currentTime) || currentTime < 0) return;

    const pct = clampPercent((currentTime / duration) * 100);
    for (const milestone of Array.from(s.milestones)) {
      if (pct < milestone) continue;
      s.milestones.delete(milestone);
      ctx.track("video_progress", {
        ...getVideoMeta(ctx, video),
        milestone,
        current_time_sec: Math.round(currentTime),
      });
    }
  };

  const onEnded = (event: Event) => {
    if (!ctx.canTrack("analytics")) return;
    if (!ctx.canTrackCategory("video_analytics")) return;
    const video =
      event.target instanceof HTMLVideoElement ? event.target : null;
    if (!video) return;
    ctx.track("video_complete", {
      ...getVideoMeta(ctx, video),
      current_time_sec: Number.isFinite(video.currentTime)
        ? Math.round(video.currentTime)
        : undefined,
    });
    // Reset milestone tracking for potential replays.
    state.delete(video);
  };

  return {
    onInit() {
      document.addEventListener("play", onPlay, true);
      document.addEventListener("timeupdate", onTimeUpdate, true);
      document.addEventListener("ended", onEnded, true);
    },
    onUnload() {
      document.removeEventListener("play", onPlay, true);
      document.removeEventListener("timeupdate", onTimeUpdate, true);
      document.removeEventListener("ended", onEnded, true);
    },
  };
});
